import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  Switch,
} from "@my-saas/ui";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import {
  useTenantSettings,
  useTestGreenApi,
  useUpdateTenantSettings,
} from "@/client/services/tenant-settings";

/**
 * Tenant settings — primarily the Green API credentials + a few engine knobs.
 * Other settings (location groups, card mappings, alert messages) get their own
 * dedicated editor pages in Phase 9.4.
 */
export const TenantSettingsPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: settings, isPending: loading } = useTenantSettings(tenantSlug);
  const update = useUpdateTenantSettings(tenantSlug);
  const test = useTestGreenApi(tenantSlug);

  // Local form state hydrated from server.
  const [form, setForm] = useState({
    green_api_instance_id: "",
    green_api_token: "",
    green_api_url: "https://api.green-api.com",
    service_enabled: true,
    oref_auto_send: true,
    send_mode: "בדיקה",
    testing_group_id: "",
    delivery_dedup_window_minutes: "10",
    oref_auto_send_freshness_minutes: "10",
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      green_api_instance_id: settings.green_api_instance_id ?? "",
      green_api_token: settings.green_api_token ?? "",
      green_api_url: settings.green_api_url ?? "https://api.green-api.com",
      service_enabled: (settings.service_enabled ?? "true") !== "false",
      oref_auto_send: (settings.oref_auto_send ?? "true") !== "false",
      send_mode: settings.send_mode ?? "בדיקה",
      testing_group_id: settings.testing_group_id ?? "",
      delivery_dedup_window_minutes: settings.delivery_dedup_window_minutes ?? "10",
      oref_auto_send_freshness_minutes: settings.oref_auto_send_freshness_minutes ?? "10",
    });
    setDirty(false);
  }, [settings]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    try {
      await update.mutateAsync(form);
      toast({ title: "Settings saved" });
      setDirty(false);
    } catch (e) {
      toast({
        variant: "error",
        title: "Save failed",
        description: (e as Error).message,
      });
    }
  };

  const runTest = async () => {
    try {
      const result = await test.mutateAsync();
      toast({
        variant: result.ok ? "default" : "error",
        title: result.ok ? "Green API reachable ✓" : "Green API failed",
        description: result.ok
          ? `HTTP ${result.status}`
          : JSON.stringify(result.data).slice(0, 200),
      });
    } catch (e) {
      toast({
        variant: "error",
        title: "Test failed",
        description: (e as Error).message,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Settings · {tenantSlug}</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Per-tenant configuration. Saving emits an event that reloads the
            in-process runtime cache — no restart needed.
          </p>
        </div>

        {/* Engine toggles */}
        <Card>
          <CardHeader>
            <CardTitle>Engine</CardTitle>
            <CardDescription>Master switches for this tenant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Service enabled</Label>
                <p className="text-xs text-muted-foreground">
                  Master gate. When off, no WhatsApp messages will be sent regardless of
                  per-card mappings.
                </p>
              </div>
              <Switch
                checked={form.service_enabled}
                onCheckedChange={(v) => set("service_enabled", v)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <Label>OREF auto-send</Label>
                <p className="text-xs text-muted-foreground">
                  Allow the fan-out service to enqueue jobs from OREF/MQTT/Tzevaadom
                  sources. Off = sources still record alerts but no WhatsApp dispatch.
                </p>
              </div>
              <Switch
                checked={form.oref_auto_send}
                onCheckedChange={(v) => set("oref_auto_send", v)}
              />
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="send_mode">Send mode</Label>
                <Input
                  id="send_mode"
                  value={form.send_mode}
                  onChange={(e) => set("send_mode", e.target.value)}
                  dir="rtl"
                />
                <p className="text-xs text-muted-foreground">
                  "בדיקה" routes all sends to <code>testing_group_id</code>. "מבצעי"
                  routes via per-card <code>language_group_defaults</code>.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="testing_group_id">Testing group id</Label>
                <Input
                  id="testing_group_id"
                  value={form.testing_group_id}
                  onChange={(e) => set("testing_group_id", e.target.value)}
                  placeholder="120363xxxxxxxxxxxx@g.us"
                />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Delivery dedup window (min)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={form.delivery_dedup_window_minutes}
                  onChange={(e) =>
                    set("delivery_dedup_window_minutes", e.target.value)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Auto-send freshness (min)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={form.oref_auto_send_freshness_minutes}
                  onChange={(e) =>
                    set("oref_auto_send_freshness_minutes", e.target.value)
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Green API credentials */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Green API
              {form.green_api_instance_id && form.green_api_token && (
                <Badge variant="secondary">configured</Badge>
              )}
            </CardTitle>
            <CardDescription>
              WhatsApp gateway credentials. Get these from your{" "}
              <a
                href="https://green-api.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Green API
              </a>{" "}
              dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="ga_instance">Instance ID</Label>
              <Input
                id="ga_instance"
                value={form.green_api_instance_id}
                onChange={(e) => set("green_api_instance_id", e.target.value)}
                placeholder="e.g. 7105123456"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ga_token">API token</Label>
              <Input
                id="ga_token"
                type="password"
                value={form.green_api_token}
                onChange={(e) => set("green_api_token", e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ga_url">Base URL</Label>
              <Input
                id="ga_url"
                value={form.green_api_url}
                onChange={(e) => set("green_api_url", e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={
                test.isPending ||
                !form.green_api_instance_id ||
                !form.green_api_token
              }
              onClick={runTest}
            >
              {test.isPending ? "Testing…" : "Test connection"}
            </Button>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 -mx-6 border-t bg-background/95 p-4 backdrop-blur">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">
              {loading ? "Loading…" : dirty ? "Unsaved changes" : "Saved"}
            </span>
            <Button onClick={save} disabled={!dirty || update.isPending}>
              {update.isPending ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
